import { Box, Button as MuiButton } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useIsDesktop } from "@src/appV2/redesign/AppLayout/useResponsive";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { Text } from "@src/appV2/redesign/components/Text";
import { TextInput } from "@src/appV2/redesign/components/TextInput";
import loginIllustration from "@src/appV2/redesign/Login/assets/login-illustration.webp";
import { DesktopLoginLayout } from "@src/appV2/redesign/Login/DesktopLoginLayout";
import { MobileLoginLayout } from "@src/appV2/redesign/Login/MobileLoginLayout";
import { berlinRebrandTheme } from "@src/appV2/redesign/theme/theme";

import { type ResetPasswordViewProps } from "./ResetPasswordView";

const PASSWORD_MIN_LENGTH = 12;

export function ResetPasswordViewRedesigned(props: ResetPasswordViewProps) {
  const {
    step,
    email,
    newPassword,
    confirmPassword,
    isSubmitting,
    error,
    hasActiveSession,
    onNewPasswordChange,
    onConfirmPasswordChange,
    onSubmit,
    onGoToLogin,
  } = props;

  const isDesktop = useIsDesktop();
  const isSuccess = step === "success";

  const contentBox = (
    <Box sx={{ width: "100%", maxWidth: 400 }}>
      {isSuccess ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Text sx={{ color: "primary.main", fontSize: "1.25rem" }}>✓</Text>
            <Text bold variant="h5" sx={{ color: "base.base" }}>
              Password reset successfully
            </Text>
          </Box>
          <Text bold variant="body1" sx={{ color: "base.muted", textAlign: "center" }}>
            Your password has been reset. You can now sign in with your new password.
          </Text>
          {!hasActiveSession && (
            <LoadingButton
              fullWidth
              isLoading={false}
              variant="primary"
              size="large"
              onClick={onGoToLogin}
            >
              Sign in with your new password
            </LoadingButton>
          )}
        </Box>
      ) : (
        <Box
          noValidate
          component="form"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            width: "100%",
          }}
          onSubmit={(formEvent: React.FormEvent) => {
            onSubmit(formEvent);
          }}
        >
          <Text bold variant="h5" sx={{ textAlign: "center", color: "base.base" }}>
            Set new password
          </Text>
          <Text variant="body2" sx={{ color: "base.muted", textAlign: "center" }}>
            Email: {email}
          </Text>

          {error && (
            <Text variant="body2" role="alert" sx={{ color: "error.main", textAlign: "center" }}>
              {error}
            </Text>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Text semibold variant="body2" sx={{ color: "base.muted" }}>
              New password
            </Text>
            <TextInput
              fullWidth
              required
              autoComplete="new-password"
              disabled={isSubmitting}
              type="password"
              value={newPassword}
              inputProps={{ "aria-label": "New password" }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "1rem",
                  backgroundColor: "background.standout",
                },
              }}
              onChange={(event) => {
                onNewPasswordChange(event.target.value);
              }}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Text semibold variant="body2" sx={{ color: "base.muted" }}>
              Confirm new password
            </Text>
            <TextInput
              fullWidth
              required
              autoComplete="new-password"
              disabled={isSubmitting}
              type="password"
              value={confirmPassword}
              inputProps={{ "aria-label": "Confirm new password" }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "1rem",
                  backgroundColor: "background.standout",
                },
              }}
              onChange={(event) => {
                onConfirmPasswordChange(event.target.value);
              }}
            />
          </Box>

          <Text variant="caption" sx={{ color: "base.muted", textAlign: "center" }}>
            Password must be at least {PASSWORD_MIN_LENGTH} characters and include uppercase and
            lowercase letters.
          </Text>

          <LoadingButton
            fullWidth
            variant="primary"
            size="large"
            type="submit"
            isLoading={isSubmitting}
            disabled={isSubmitting || !newPassword || !confirmPassword || !email}
          >
            Set new password
          </LoadingButton>

          {!hasActiveSession && (
            <MuiButton
              variant="text"
              sx={{
                textTransform: "none",
                color: "primary.main",
                alignSelf: "center",
              }}
              onClick={onGoToLogin}
            >
              Back to sign in with password
            </MuiButton>
          )}
        </Box>
      )}
    </Box>
  );

  const showIllustration = !isSuccess;

  if (isDesktop) {
    return (
      <ThemeProvider theme={berlinRebrandTheme}>
        <DesktopLoginLayout>
          {showIllustration && (
            <img
              src={loginIllustration}
              alt="Person sitting at a desk"
              style={{ height: "min(212px, 22vh)", width: "auto" }}
            />
          )}
          {contentBox}
        </DesktopLoginLayout>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={berlinRebrandTheme}>
      <MobileLoginLayout>
        {showIllustration && (
          <img
            src={loginIllustration}
            alt="Person sitting at a desk"
            style={{ height: 170, width: "auto", marginBottom: 40 }}
          />
        )}
        {contentBox}
      </MobileLoginLayout>
    </ThemeProvider>
  );
}
